<template>
  <div class="wide main-content">
    <div class="top-wrap">
      <div
        v-if="backgroundUrl"
        class="clp-banner content-block"
        :style="backgroundUrl"
      />
      <div class="top-content content-block">
        <h1>{{ pageData.title }}</h1>
        <div
          v-if="pageData.introText"
          v-dompurify-html="pageData.introText"
          class="intro-text-block"
        />
      </div>

      <count-down-block
        v-if="timerEnabled"
        class="content-block"
        :type="'green'"
        :deadline="deadline"
        :title="timerTitle"
      />

      <book-and-travel
        v-if="pageData.flightDetails.book.title || pageData.flightDetails.travel.title"
        class="content-block"
        :book="pageData.flightDetails.book"
        :travel="pageData.flightDetails.travel"
      />
    </div>

    <div class="content-block">
      <h2>{{ pageData.subTitle }}</h2>
      <div
        v-if="!isFiltersBlockHidden"
        class="search-form-wrap"
      >
        <div class="search-form">
          <div class="origin">
            <origin
              ref="origin"
              v-focusout="onOriginOpenUpdate"
              class="e-horizon-orig-destin clp-origin"
              :choose="$t('searchForm.originDestin.origin.originChooseEHorizon')"
              :class="[{ error: origin.invalid }]"
              :filter="origin.typeahead.filter"
              :is-mobile="deviceType"
              :label="$t('campaignLandingPage.filter.selectOrigin')"
              :open="origin.typeahead.open"
              :options="filteredAirports"
              :placeholder="$t('searchForm.originDestin.origin.placeholderEHorizon')"
              :selected-label="origin.selectedLabel"
              :touched="origin.typeaheadIgnoreFilter"
              :translation-no-results="$t('searchForm.originDestin.noResultsEHorizon')"
              :translation-invalid-language="wrongInputLangTranslation"
              :value="origin.value"
              @focused="showOriginTypeahead"
              @update:filter="onOriginFilter"
              @update:open="onOriginOpenUpdate"
              @update:label="onOriginLabelUpdate"
              @update:value="onOriginSelect"
            />
          </div>
          <div class="destin">
            <input-field
              :placeholder="$t('campaignLandingPage.filter.searchByCityOrCountry')"
              :name="'filterDestinations'"
              :value="filter"
              @input="filterDestinations"
            />
          </div>
          <div class="sorter">
            <dropdown-simple
              :items="sorterOptions"
              :device-type="deviceType"
              :mobile-title="$t('campaignLandingPage.filter.sort')"
              :selected-title="$t('campaignLandingPage.filter.sort')"
              :selected-item="selectedSorter"
              @select="selectSorter"
            />
          </div>
        </div>
      </div>

      <histogram-legend />

      <div class="flights-block">
        <flight-item
          v-for="flight in filteredFlights"
          :key="`flight-${flight.originCode}-${flight.code}`"
          :flight="flight"
          :origin-code="flight.originCode"
          :trip-type="flight.tripType"
          :hide-images="hideImages"
          :is-ond="isOndCampaign"
          :icon-type="iconType"
          :open-all-histograms="openAllHistograms"
        />
      </div>

      <div class="bottom-block">
        <terms-and-conditions
          v-if="termsAndConditions && termsAndConditions.content"
          :title="termsAndConditions.title"
          :content="termsAndConditions.content"
          :always-opened="true"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters } from 'vuex'
  import ImageHelpers from 'utils/mixins/ImageHelpers'
  import Focusout from 'utils/directives/FocusOut'
  import OriginMixin from 'components/eHorizon/SearchFormMixins/Origin'
  import SearchForm from 'components/eHorizon/SearchFormMixins/SearchForm'

  export default {
    components: {
      'count-down-block': () =>
        import(
          /* webpackChunkName: "count-down-block" */ 'containers/Pages/LandingPage/CountDownBlock'
        ),
      'dropdown-simple': () =>
        import(
          /* webpackChunkName: "dropdown-simple" */ 'components/Dropdown/DropdownSimple'
        ),
      'terms-and-conditions': () =>
        import(
          /* webpackChunkName: "terms-and-conditions" */ 'components/TermsAndConditions'
        ),
      'book-and-travel': () =>
        import(
          /* webpackChunkName: "book-and-travel" */ 'containers/Pages/LandingPage/BookAndTravel'
        ),
      'flight-item': () =>
        import(
          /* webpackChunkName: "clp-flight-item" */ 'containers/Pages/LandingPage/FlightItem'
        ),
      'input-field': () =>
        import(/* webpackChunkName: "input-field" */ 'components/Forms/Input'),
      'histogram-legend': () =>
        import(
          /* webpackChunkName: "histogram-legend" */ 'components/Histogram/HistogramLegend'
        ),
      Origin: () =>
        import(
          /* webpackChunkName: "clp-origin-select" */ 'components/eHorizon/Destination/Origin'
        )
    },
    directives: {
      Focusout
    },
    mixins: [ImageHelpers, OriginMixin, SearchForm],
    data() {
      return {
        originItems: [],
        filter: '',
        sorterOptions: [
          {
            value: 'priceAsc',
            title: this.$t('reusable.sorters.priceAsc')
          },
          {
            value: 'priceDesc',
            title: this.$t('reusable.sorters.priceDesc')
          },
          {
            value: 'cityAsc',
            title: this.$t('reusable.sorters.destinationAsc')
          },
          {
            value: 'cityDesc',
            title: this.$t('reusable.sorters.destinationDesc')
          }
        ]
      }
    },
    computed: {
      ...mapState('device', ['deviceType']),

      ...mapGetters('campaignLandingDestinations', ['filteredFlights', 'airports']),
      ...mapState('campaignLandingDestinations', [
        'pageData',
        'timerTitle',
        'timerEnabled',
        'deadline',
        'selectedSorter',
        'termsAndConditions',
        'iconType',
        'clpOrigDestData',
        'hideFilterBlock',
        'hideImages',
        'isOndCampaign',
        'openAllHistograms'
      ]),

      backgroundUrl() {
        const source = {
          desktop: this.pageData.desktopImg,
          tablet: this.pageData.tabletImg,
          mobile: this.pageData.mobileImg
        }

        return source[this.deviceType]
          ? this.$_ImageHelpers_getBackgroundImage(source[this.deviceType])
          : ''
      },

      isFiltersBlockHidden() {
        return this.isXlsEmpty || this.hideFilterBlock
      },

      isXlsEmpty() {
        return !Object.keys(this.clpOrigDestData).length
      },

      filteredAirports() {
        const searchResults = this.$t('searchForm.originDestin.origin.matchedOrigins')

        if (!this.airports.includes(searchResults)) {
          return this.airports
        }

        return this.airports.filter((item) => item !== searchResults)
      },

      wrongInputLangTranslation() {
        return this.$i18n.locale() === 'ru'
          ? this.$t('searchForm.originDestin.invalidLanguageEHorizonCyrillic')
          : this.$t('searchForm.originDestin.invalidLanguageEHorizon')
      }
    },

    methods: {
      filterDestinations(filter) {
        this.$store.commit(
          'campaignLandingDestinations/setSelectedDestinationFilter',
          filter
        )
      },

      selectSorter(sorter) {
        this.$store.dispatch('campaignLandingDestinations/setSelectedSorter', sorter)
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';

  .main-content {
    color: $blue-dark;
    background-color: $blue-bg-css;
  }

  .top-wrap {
    background: white;
  }

  .content-block {
    @include bp-mf-tablet {
      max-width: 1120px;
      margin: 0 auto;
    }
  }

  .clp-banner {
    width: 100%;
    height: 200px;
    background-repeat: no-repeat;
    background-size: cover;
    background-position: center center;

    @include bp-tablet-only {
      height: 325px;
    }

    @include bp-mf-desktop {
      height: 375px;
    }
  }

  .top-content {
    text-align: center;
    margin: 0 20px;
    padding: 28px 0;
    border-bottom: 1px solid $gray-divider;

    @include bp-mf-desktop {
      margin: 0 auto;
    }
  }

  h1 {
    font-size: rem(32, 16);
    font-weight: 500;
    line-height: rem(42, 16);
    margin: 0 0 16px;

    @include bp-mf-tablet {
      font-size: rem(40, 16);
    }
  }

  .intro-text-block {
    font-size: rem(20, 16);
    font-weight: 300;
    line-height: rem(28, 16);

    @include bp-mf-tablet {
      padding: 0;
      line-height: rem(32, 16);
    }

    @include bp-mf-desktop {
      padding: 0 20px;
    }

    ::v-deep {
      p {
        margin: 0;
      }
      strong {
        font-weight: 500;
      }
      a {
        color: $blue-medium;

        &:hover {
          @include bp-mf-tablet {
            color: $blue-light;
          }
        }
      }
    }
  }

  .countdown-block {
    padding: 24px 0;
    border-bottom: 1px solid $gray-divider;

    @include bp-mf-tablet {
      padding: 28px 0 32px;
    }
  }

  .flights-block {
    margin: 20px;

    @include bp-mf-tablet {
      display: flex;
      flex-wrap: wrap;
    }

    @include bp-mf-desktop {
      margin: 20px 0;
    }

    @include bp-desktop-small {
      margin: 20px;
    }
  }

  h2 {
    color: $blue-dark;
    font-size: rem(24, 16);
    font-weight: 500;
    padding: 27px 20px 25px;
    margin: 0;
    background-color: $blue-bg-css;

    @include bp-mf-tablet {
      padding: 37px 25px 25px;
    }

    @include bp-mf-desktop {
      padding-left: 0;
    }

    @include bp-desktop-small {
      padding-left: 20px;
    }
  }

  .search-form-wrap {
    padding: 24px 20px;
    background-color: $scn-gray;

    ::v-deep {
      .field-group {
        margin: 12px 0;

        @include bp-mf-tablet {
          margin: 0;
        }
      }
    }

    .search-form {
      @include bp-mf-tablet {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
    }

    .destin {
      @include bp-mf-tablet {
        width: 100%;
        margin: 0 20px 0 12px;
      }

      ::v-deep {
        input {
          color: $blue-dark;

          @include input-placeholder {
            color: $text-secondary;
          }
        }
      }
    }
    .origin {
      .clp-origin {
        min-width: 155px;
        height: 48px;
        border-radius: 3px;
        border: 1px solid $blue-border;

        @include bp-mf-tablet {
          max-width: 155px;
        }

        ::v-deep {
          .input-container {
            .placeholder {
              font-size: rem(14, 16);
              top: 22px;
              max-width: calc(100% - 24px);
              @include text-overflow();

              b {
                font-weight: 400;
              }
            }

            &.visible {
              @include bp-mf-tablet {
                z-index: 2;
                top: 10px;
                left: 10px;
              }
              @include bp-mf-desktop {
                top: 0;
                left: 0;

                input {
                  top: 12px;
                }
              }
            }
          }
          .dropdown-menu {
            @include bp-mf-tablet {
              z-index: 1;
              top: 0;
              left: 0;

              .close-icon {
                top: 10px;
                right: 10px;
              }
            }
          }
        }
      }
    }

    .origin ::v-deep .dropdown-wrap,
    .destin ::v-deep input,
    .sorter {
      @include bp-mobile {
        height: 56px;
        border-radius: 4px;
        border: 1px solid $blue-border;
      }
    }

    .sorter {
      @include bp-mobile {
        display: flex;
        justify-content: space-between;
        align-items: center;
        border: 1px solid rgba($blue-dark, 0.2);
      }

      ::v-deep {
        .dropdown-field {
          font-weight: 500;
          background: transparent;
          border-radius: 4px;
          padding: 0;
          min-width: 32px;

          &__title {
            @include bp-mf-tablet {
              display: none;
            }
          }

          @include bp-mobile {
            border: none;
            padding-left: 16px;
          }

          @include bp-mf-tablet {
            height: 32px;
          }

          &:after {
            @include icon-font-thin();
            font-size: rem(22, 16);
            width: 32px;
            height: 32px;
            content: $icon-sort;
            vertical-align: middle;
            transform: rotate(0) !important;
            top: calc(50% - 11px);
            right: 0;
            color: $blue-medium;

            @include bp-mf-tablet {
              right: calc(50% - 21px);
            }
          }
        }

        .dropdown-wrap {
          &.open .dropdown-field {
            border: 1px solid $blue-medium;
          }
        }

        .dropdown-list {
          min-width: 258px;
          right: 0;
          left: auto;
          top: 34px;
          max-height: none;
          z-index: 4;

          li {
            font-size: rem(16, 16);
            line-height: rem(55, 16);
          }
        }
      }
    }
  }

  ::v-deep {
    .histogram-legend {
      display: flex;
      margin: 20px 0 0 20px !important;

      @include bp-mf-desktop {
        margin: 20px 0 0 0 !important;
      }

      .legend {
        margin-bottom: 0;
      }

      .direct {
        margin-right: 40px;
      }
    }
  }

  .bottom-block {
    .terms-and-conditions-block {
      text-align: left;
      border-top: 1px solid $blue-dividers;
      margin: 0;

      .terms-and-conditions-panel-title {
        margin: 28px 0 20px;
        line-height: rem(31, 16) !important;
        height: auto !important;
        color: $blue-dark !important;

        &:before {
          color: rgba($blue-dark, 0.32) !important;
        }

        &:after {
          display: none !important;
        }
      }
    }
  }
</style>
